// client/src/config/app-meta.ts — the build-time environment, read in ONE place · UI-004.
//
// Every `import.meta.env` read in the application happens here. Two reasons, both learned
// from the shape of §6.4: the footer and the support modal need the same values, and a value
// that is MISSING has to be distinguishable from one that is empty — a deployment that never
// set `VITE_SUPPORT_WHATSAPP_URL` must not render a „Grup de suport WhatsApp" button pointing
// at `href=""`, which navigates to the current page and looks like a broken app.
//
// READ AT CALL TIME, not at module load. Vite inlines `import.meta.env.VITE_X` wherever it
// appears, inside a function body as readily as at the top level, so the bundle is identical
// either way — but a module-level `const` freezes the environment at import, and a test can
// then no longer say „given a deployment that configured a WhatsApp group". The values are
// build-time constants, so the call costs an object literal.
//
// The reads are static member expressions on purpose: a computed lookup (`env[name]`) is
// documented as unsupported in a production build.

/** `''`, `'   '` and an undeclared variable are all „not configured". */
function configured(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

export interface AppMeta {
  /**
   * The §6.4 footer version stamp. `dev` is what an unstamped build honestly is — the image
   * build takes the real one through `--build-arg APP_VERSION` (deploy/Dockerfile.client).
   */
  version: string;
  /** §6.4's four support/legal addresses; `undefined` where the deployment set none. */
  supportWhatsappUrl: string | undefined;
  supportEmail: string | undefined;
  manualUrl: string | undefined;
  termsUrl: string | undefined;
  privacyUrl: string | undefined;
  contactUrl: string | undefined;
}

/**
 * Base URL of the API (§2.5 `VITE_API_BASE_URL`), without a trailing slash so a path can be
 * appended verbatim. `/` — same-origin — in every deployed environment (02 §16); a dev
 * server pointing elsewhere sets it explicitly. Read here because this module is the only one
 * allowed to read `import.meta.env` (D-349).
 */
export function apiBaseUrl(): string {
  const configured_ = configured(import.meta.env.VITE_API_BASE_URL) ?? '/';
  return configured_.endsWith('/') ? configured_.slice(0, -1) : configured_;
}

export function appMeta(): AppMeta {
  return {
    version: configured(import.meta.env.VITE_APP_VERSION) ?? 'dev',
    supportWhatsappUrl: configured(import.meta.env.VITE_SUPPORT_WHATSAPP_URL),
    supportEmail: configured(import.meta.env.VITE_SUPPORT_EMAIL),
    manualUrl: configured(import.meta.env.VITE_MANUAL_URL),
    termsUrl: configured(import.meta.env.VITE_TERMS_URL),
    privacyUrl: configured(import.meta.env.VITE_PRIVACY_URL),
    contactUrl: configured(import.meta.env.VITE_CONTACT_URL),
  };
}

/**
 * True for an address that leaves the SPA. `VITE_MANUAL_URL` is `/manual` by default but is
 * an absolute URL wherever the manual is hosted elsewhere, and the three legal links always
 * are — so „new tab or not" is a property of the value, never of the link that renders it.
 */
export function isExternalUrl(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/**
 * A `mailto:` for the support mailbox, or `undefined` when no mailbox is configured.
 *
 * `URLSearchParams` encodes a space as `+`, which is correct for a query string and wrong
 * here: mail clients paste the `+` into the subject line literally. Percent-encoding is what
 * RFC 6068 asks for, so the `+` is put back as `%20`.
 */
export function supportMailto(
  mailbox: string | undefined,
  subject: string,
  body?: string,
): string | undefined {
  if (mailbox === undefined) return undefined;
  const params = new URLSearchParams({ subject });
  if (body !== undefined) params.set('body', body);
  return `mailto:${mailbox}?${params.toString().replace(/\+/g, '%20')}`;
}
